/**
 * Catálogos de la Factura Electrónica en Línea (FEL) de la SAT de Guatemala:
 * tipos de Documento Tributario Electrónico (DTE), afiliación al IVA,
 * estados del documento en Tumin, certificadores, frases y escenarios de
 * exención. Los códigos (`FACT`, `GEN`, tipo/escenario de frase) son los del
 * esquema y catálogos publicados por la SAT para el régimen FEL.
 */
import { keyedEnum } from '../catalogs/enum.js';
// ── Tipos de DTE ────────────────────────────────────────────────────────────
/**
 * Tipos de DTE que emite Tumin:
 * - `FACT`: factura por los intereses, la mora, los gastos de cobranza y su IVA.
 * - `NCRE`: nota de crédito que revierte una factura de un mes ya cerrado
 *   (un pago reversado después de declarado el período).
 */
export const FEL_DTE_TYPES = ['FACT', 'NCRE'];
/** Catálogo con clave: `FEL_DTE_TYPE.FACT`. */
export const FEL_DTE_TYPE = keyedEnum(FEL_DTE_TYPES);
/** Nombre en español de cada tipo de DTE. */
export const FEL_DTE_TYPE_LABELS = {
    FACT: 'Factura',
    NCRE: 'Nota de crédito',
};
// ── Afiliación al IVA del emisor ────────────────────────────────────────────
/**
 * Afiliación al IVA del emisor, según su inscripción en el RTU:
 * - `GEN`: régimen general (IVA 12 % desglosado en cada línea).
 * - `PEQ`: pequeño contribuyente (sin desglose de IVA; no genera crédito fiscal).
 * - `EXE`: emisor exento (todas las líneas exentas con su frase de exención).
 */
export const FEL_IVA_AFFILIATIONS = ['GEN', 'PEQ', 'EXE'];
/** Catálogo con clave: `FEL_IVA_AFFILIATION.GEN`. */
export const FEL_IVA_AFFILIATION = keyedEnum(FEL_IVA_AFFILIATIONS);
/** Nombre en español de cada afiliación. */
export const FEL_IVA_AFFILIATION_LABELS = {
    GEN: 'Régimen general (IVA 12 %)',
    PEQ: 'Pequeño contribuyente',
    EXE: 'Exento de IVA',
};
// ── Estados del documento en Tumin ──────────────────────────────────────────
/**
 * Ciclo de vida de un documento FEL en Tumin:
 * - `pending`: creado junto con el pago; espera al certificador.
 * - `certified`: la SAT lo autorizó (tiene UUID, serie y número).
 * - `failed`: el certificador lo rechazó o no respondió; se reintenta.
 * - `annulled`: anulado ante la SAT (pago reversado en el mismo mes).
 * - `not_required`: ya no se emitirá (el pago se reversó antes de certificarlo).
 */
export const FEL_DOCUMENT_STATUSES = ['pending', 'certified', 'failed', 'annulled', 'not_required'];
/** Catálogo con clave: `FEL_DOCUMENT_STATUS.certified`. */
export const FEL_DOCUMENT_STATUS = keyedEnum(FEL_DOCUMENT_STATUSES);
/** Etiquetas en español de cada estado. */
export const FEL_DOCUMENT_STATUS_LABELS = {
    pending: 'En certificación',
    certified: 'Certificada',
    failed: 'Con error',
    annulled: 'Anulada',
    not_required: 'No requerida',
};
// ── Certificadores ──────────────────────────────────────────────────────────
/**
 * Certificadores soportados:
 * - `infile`: INFILE, S.A., el certificador autorizado por la SAT más usado en Guatemala.
 * - `sandbox`: simulador determinista (pruebas, desarrollo y demostración). Sus
 *   documentos NO tienen validez fiscal.
 */
export const FEL_CERTIFIERS = ['infile', 'sandbox'];
/** Catálogo con clave: `FEL_CERTIFIER.infile`. */
export const FEL_CERTIFIER = keyedEnum(FEL_CERTIFIERS);
/** Nombre de cada certificador. */
export const FEL_CERTIFIER_LABELS = {
    infile: 'INFILE',
    sandbox: 'Simulador (sin validez fiscal)',
};
// ── Tipo de impuesto de las líneas ──────────────────────────────────────────
/**
 * Tratamiento del IVA en las líneas de una factura:
 * - `taxable`: IVA desglosado (emisor GEN que cobró IVA).
 * - `exempt`: líneas exentas con frase de exención (cooperativas con sus asociados, ONG exentas).
 * - `small_taxpayer`: pequeño contribuyente, sin desglose de IVA.
 */
export const FEL_TAX_MODES = ['taxable', 'exempt', 'small_taxpayer'];
/** Catálogo con clave: `FEL_TAX_MODE.exempt`. */
export const FEL_TAX_MODE = keyedEnum(FEL_TAX_MODES);
/** Etiquetas en español del tratamiento del IVA. */
export const FEL_TAX_MODE_LABELS = {
    taxable: 'Gravada con IVA',
    exempt: 'Exenta de IVA',
    small_taxpayer: 'Pequeño contribuyente',
};
// ── Receptor ────────────────────────────────────────────────────────────────
/**
 * Cómo se identifica al receptor de la factura:
 * - `nit`: NIT válido del cliente.
 * - `cui`: DPI del cliente (obligatorio para consumidor final desde el umbral de la SAT).
 * - `final_consumer`: "CF" (consumidor final).
 */
export const FEL_RECEIVER_KINDS = ['nit', 'cui', 'final_consumer'];
/** Catálogo con clave: `FEL_RECEIVER_KIND.final_consumer`. */
export const FEL_RECEIVER_KIND = keyedEnum(FEL_RECEIVER_KINDS);
/** Etiquetas en español del tipo de receptor. */
export const FEL_RECEIVER_KIND_LABELS = {
    nit: 'NIT',
    cui: 'DPI (CUI)',
    final_consumer: 'Consumidor final',
};
// ── Conceptos facturables ───────────────────────────────────────────────────
/**
 * Conceptos que se facturan de un pago. El capital NO se factura: devolver
 * el dinero prestado no es una venta ni un servicio (Ley del IVA, art. 2 y 3).
 */
export const FEL_LINE_KINDS = ['interest', 'late_interest', 'late_fee'];
/** Catálogo con clave: `FEL_LINE_KIND.late_fee`. */
export const FEL_LINE_KIND = keyedEnum(FEL_LINE_KINDS);
/** Descripción base de cada concepto en la factura. */
export const FEL_LINE_KIND_LABELS = {
    interest: 'Intereses corrientes',
    late_interest: 'Intereses moratorios',
    late_fee: 'Gastos de cobranza',
};
/** Tipo de frase de la SAT para el régimen del ISR del emisor. */
export const FEL_PHRASE_TYPE_ISR = 1;
/** Tipo de frase de la SAT para los agentes de retención del IVA. */
export const FEL_PHRASE_TYPE_IVA_WITHHOLDING = 2;
/** Tipo de frase de la SAT de pequeño contribuyente ("no genera derecho a crédito fiscal"). */
export const FEL_PHRASE_TYPE_SMALL_TAXPAYER = 3;
/** Tipo de frase de la SAT para operaciones exentas o no afectas al IVA. */
export const FEL_PHRASE_TYPE_EXEMPT = 4;
/**
 * Frases generales que la institución puede declarar en sus facturas
 * (catálogo de frases de la SAT). La del régimen del ISR es obligatoria en
 * las facturas de emisores del régimen general.
 */
export const FEL_PHRASES = [
    { type: FEL_PHRASE_TYPE_ISR, scenario: 1, label: 'Sujeto a pagos trimestrales ISR' },
    { type: FEL_PHRASE_TYPE_ISR, scenario: 2, label: 'Sujeto a retención definitiva ISR' },
    { type: FEL_PHRASE_TYPE_ISR, scenario: 3, label: 'Sujeto a pago directo ISR' },
    { type: FEL_PHRASE_TYPE_IVA_WITHHOLDING, scenario: 1, label: 'Agente de retención del IVA' },
    { type: FEL_PHRASE_TYPE_SMALL_TAXPAYER, scenario: 1, label: 'No genera derecho a crédito fiscal' },
];
/**
 * Escenarios de exención del IVA (frase tipo 4 de la SAT) aplicables a una
 * institución de crédito. SUPUESTO: la numeración y leyendas se tomaron del
 * catálogo de frases publicado por la SAT para FEL; deben verificarse con el
 * catálogo vigente del certificador y con el asesor fiscal de la entidad
 * antes de activar la facturación exenta.
 */
export const FEL_EXEMPTION_SCENARIOS = [
    { type: FEL_PHRASE_TYPE_EXEMPT, scenario: 1, label: 'Exenta del IVA (art. 7 num. 2 Ley del IVA)' },
    { type: FEL_PHRASE_TYPE_EXEMPT, scenario: 2, label: 'Exenta del IVA (art. 7 num. 4 Ley del IVA)' },
    { type: FEL_PHRASE_TYPE_EXEMPT, scenario: 3, label: 'Exenta del IVA (art. 7 num. 5 Ley del IVA)' },
    { type: FEL_PHRASE_TYPE_EXEMPT, scenario: 4, label: 'Exenta del IVA (art. 7 num. 9 Ley del IVA)' },
    { type: FEL_PHRASE_TYPE_EXEMPT, scenario: 5, label: 'Exenta del IVA (art. 7 num. 10 Ley del IVA)' },
    { type: FEL_PHRASE_TYPE_EXEMPT, scenario: 6, label: 'Exenta del IVA (art. 7 num. 13 Ley del IVA)' },
    { type: FEL_PHRASE_TYPE_EXEMPT, scenario: 7, label: 'Exenta del IVA (art. 7 num. 14 Ley del IVA)' },
    { type: FEL_PHRASE_TYPE_EXEMPT, scenario: 8, label: 'Exenta del IVA (art. 8 num. 1 Ley del IVA)' },
];
/** Leyenda de una frase (o `null` si no está en los catálogos de Tumin). */
export function felPhraseLabel(phrase) {
    const found = [...FEL_PHRASES, ...FEL_EXEMPTION_SCENARIOS].find((p) => p.type === phrase.type && p.scenario === phrase.scenario);
    return found?.label ?? null;
}
/** Leyenda que la representación impresa de un DTE certificado debe llevar. */
export const FEL_DTE_LEGEND = 'Documento Tributario Electrónico';
//# sourceMappingURL=catalog.js.map